// src/hooks/useChat.ts
import { useState, useCallback, useRef } from 'react';
import { sendChat, sendFeedback, fetchConversations, fetchConversation, deleteConversation } from '../api/client';
import type { Message, Conversation } from '../types';

export function useChat(personaId: string) {
  const [messages, setMessages]         = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [isLoading, setIsLoading]       = useState(false);
  const sessionId = useRef(`session_${Date.now()}`);
  const convId    = useRef<string | null>(null);

  const loadConversations = useCallback(async () => {
    const data = await fetchConversations(personaId);
    setConversations(data);
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    const data = await fetchConversation(id);
    // Map loaded messages (they don't have IDs from DB)
    const mapped: Message[] = data.messages.map((m, i) => ({
      ...m,
      id: `loaded_${i}`,
      timestamp: new Date().toISOString(),
    }));
    setMessages(mapped);
    setActiveConvId(id);
    convId.current = id;
  }, []);

  const removeConversation = useCallback(async (id: string) => {
    await deleteConversation(id);
    if (id === activeConvId) {
      setMessages([]);
      setActiveConvId(null);
      convId.current = null;
      sessionId.current = `session_${Date.now()}`;
    }
    await loadConversations();
  }, [activeConvId, loadConversations]);

  const newChat = useCallback(() => {
    setMessages([]);
    setActiveConvId(null);
    convId.current = null;
    sessionId.current = `session_${Date.now()}`;
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: Message = {
      id:        `u_${Date.now()}`,
      role:      'user',
      content:   text.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const data = await sendChat({
        message:         text.trim(),
        session_id:      sessionId.current,
        conversation_id: convId.current,
        active_persona:  personaId,
      });

      if (data.conversation_id) {
        convId.current = data.conversation_id;
        setActiveConvId(data.conversation_id);
      }

      const assistantMsg: Message = {
        id:        `a_${Date.now()}`,
        role:      'assistant',
        content:   data.reply,
        timestamp: new Date().toISOString(),
        memories:  data.retrieved_memories,
        reasoning: data.reasoning,
        point_id:  data.point_id,
        feedback:  0,
        isSupport: data.path === 'SUPPORT',
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Refresh sidebar history
      setTimeout(loadConversations, 600);
    } catch {
      setMessages(prev => [...prev, {
        id:        `err_${Date.now()}`,
        role:      'assistant',
        content:   '⚠ Could not reach the backend. Make sure the FastAPI server is running:\n\nuvicorn fastapi_backend:app --reload --port 8000',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, personaId, loadConversations]);

  const rateFeedback = useCallback(async (msgId: string, pointId: string, rating: 1 | -1) => {
    await sendFeedback(pointId, rating);
    setMessages(prev =>
      prev.map(m => m.id === msgId ? { ...m, feedback: rating } : m)
    );
  }, []);

  return {
    messages, conversations, activeConvId, isLoading,
    loadConversations, loadConversation, removeConversation,
    newChat, sendMessage, rateFeedback,
  };
}
